import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

interface ConferenciaItem {
  seq: number;
  codprod: number;
  descricao: string;
  refforn?: string;
  condicao?: string;
  local?: string;
  unidade: string;
  qtd: number;
  conferido: boolean;
  observacao: string;
  /** Quantidade digitada pelo conferente (conferência cega — não vê "qtd"
   *  esperada enquanto conta). Reconstruída ao recarregar a partir da nota
   *  de divergência gravada em "observacao" (ver MARCADOR_DIVERGENCIA). */
  qtdConferida?: number | null;
  qtdConferidaStr?: string;
  /** true quando qtdConferida difere de qtd. */
  divergente?: boolean;
  // "Item no outro Galpão" -- pendência que sai da fila "A Conferir" sem
  // marcar CONFERIDO='S' (mesmo recurso já existente na Separação nativa,
  // ver PendenciaSeparacaoComponent/estoque-separacao-itens.component.ts).
  // true enquanto a pendência estiver ABERTA (ninguém buscou ainda).
  pendente?: boolean;
}

interface UsuarioConf {
  codusu: number;
  nomeusu: string;
}

@Component({
  selector: 'app-estoque-conferencia-itens',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-conferencia-itens.component.html',
  styleUrls: ['./estoque-conferencia-itens.component.css']
})
export class EstoqueConferenciaItensComponent implements OnInit {
  @Input() nunota!: number;
  @Output() voltar = new EventEmitter<void>();
  @Output() finalizado = new EventEmitter<void>();
  /** Emitido depois que o addon nativo finaliza a conferência (Finalizar
   *  ou Cliente Retira) com sucesso — pede pro gateway sobrescrever o
   *  GPL_STATUS pra "PRONTO" via gravação silenciosa (mesmo mecanismo do
   *  Cancelar/Estornar). O gateway chama de volta resolverStatusFinal(...)
   *  quando essa gravação terminar. */
  @Output() solicitarFinalizarComStatus = new EventEmitter<{ nunota: number; novoGplStatus: string }>();

  itens: ConferenciaItem[] = [];
  carregando = false;
  erro = '';
  erroSalvar = '';
  clienteNome = '';
  vendedor = '';
  /** Nº da TAG informado ao iniciar a Separação (GPL_SEPARACAO_ESTOQUE.NUMTAG) —
   *  mostrado aqui também pra o conferente não precisar voltar na Separação. */
  numTag = '';
  /** Observação de Entrega (TGFCAB.AD_FRETEOBS) — mesmo campo exibido na aba
   *  "Entrega" do Pedido de Venda; aqui é só leitura, pro conferente ver
   *  instruções especiais de entrega/frete sem precisar abrir o pedido. */
  obsEntrega = '';

  abaAtiva: 'pendentes' | 'conferidos' = 'pendentes';

  allDone = false;
  finalizando = false;
  erroFinalizar = '';
  modalFinalizar = false;

  /** Modal de divergências — aberto no lugar do modalFinalizar quando,
   *  ao concluir a conferência, existem itens com quantidade contada
   *  diferente da esperada. */
  modalDivergencias = false;

  // Observação expandida por item (seq → editing)
  obsEditando: Set<number> = new Set();

  /** Conferência cega orientada por busca: o conferente digita o código/PN
   *  do produto e só então o item aparece (em vez de mostrar a lista
   *  inteira de itens pendentes, o que revelaria o que falta contar). */
  buscaPn = '';
  @ViewChild('buscaPnInput') buscaPnInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('qtdContadaInput') qtdContadaInputRef?: ElementRef<HTMLInputElement>;

  /** Prefixo fixo usado para gravar e depois reconhecer a nota de
   *  divergência dentro do campo "observacao" (único lugar que o addon
   *  doSalvarItemConferencia realmente persiste, além do booleano
   *  "conferido") — não existe coluna própria de quantidade conferida.
   *  Só ASCII (sem "⚠" nem acentos): o campo OBSERVACAO passa pelo addon do
   *  Sankhya e, dependendo do charset da coluna no banco, símbolos fora do
   *  Latin-1 (como "⚠") voltam corrompidos (viram "?"), quebrando o
   *  reconhecimento do marcador ao recarregar a tela — o item aparecia
   *  como conferido normal (verde) mesmo estando divergente. */
  private static readonly MARCADOR_DIVERGENCIA = '[DIVERGENCIA - CONFERENCIA CEGA]';

  /** Reconhece também o marcador antigo (com "⚠ Divergência (conferência
   *  cega):"), usado antes da correção acima — inclusive já corrompido em
   *  "?" — pra não perder a divergência de itens já gravados. */
  private static readonly MARCADOR_DIVERGENCIA_LEGADO_RE = '.?\\s*Diverg[êe]ncia\\s*\\(confer[êe]ncia cega\\):';

  // ── Transferir Conferência (trocar de conferente) ──────────────────────
  // Mesmo padrão do "Transferir" da Separação nativa (ver
  // estoque-separacao-itens.component.ts) -- permite passar a conferência
  // em andamento para outro conferente, sem perder o progresso já feito.
  modalTransferir = false;
  buscaTransferir = '';
  usuariosTransferir: UsuarioConf[] = [];
  buscandoTransferir = false;
  selecionadoTransferir: UsuarioConf | null = null;
  transferindo = false;
  erroTransferir = '';
  private _buscaTransferirTimer: any;

  constructor(private gateway: SankhyaGatewayService) {}

  ngOnInit(): void {
    if (this.nunota) this.carregarItens();
  }

  carregarItens(): void {
    this.carregando = true;
    this.erro = '';
    this.gateway.buscarDetalhesPedidoVenda(this.nunota).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        const det = body?.detalhe ?? body;
        this.clienteNome = det?.cliente ?? '';
        this.vendedor    = det?.vendedor ?? '';
        this.obsEntrega  = det?.adFreteObs ?? '';

        const lista = det?.itens ?? [];
        this.itens = lista.map((it: any) => ({
          seq:       it.seq ?? 0,
          codprod:   it.codprod ?? 0,
          descricao: it.descricao ?? '',
          refforn:   it.refforn ?? '',
          condicao:  it.condicao ?? '',
          local:     it.local ?? '',
          unidade:   it.codvol ?? 'UN',
          qtd:       Number(it.qtd ?? 0),
          conferido: false,
          observacao: ''
        }));

        // Carrega estado já conferido
        this.gateway.getEstadoItensConferencia(this.nunota).subscribe({
          next: (estado: any) => {
            if (estado?.sucesso) {
              this.numTag = estado.numTag ?? '';
              const conferidos = new Set<number>(estado.seqsConferidos ?? []);
              const obsMap = new Map<number, string>();
              for (const o of (estado.observacoes ?? [])) {
                obsMap.set(Number(o.sequencia), o.observacao);
              }
              this.itens.forEach(it => {
                it.conferido = conferidos.has(it.seq);
                it.observacao = obsMap.get(it.seq) ?? '';
                this._reconstruirDivergencia(it);
                if (it.observacao) this.obsEditando.add(it.seq);
              });
              this.allDone = this.itens.length > 0 && this.itens.every(it => it.conferido);
              // Marca itens com pendência "Item no outro Galpão" ABERTA -- saem
              // da fila "A Conferir" (ver itensPendentes) sem estar CONFERIDO='S'.
              this.carregarPendenciasGalpao();
            }
            this.carregando = false;
          },
          error: () => { this.carregando = false; }
        });
      },
      error: (err: any) => {
        this.erro = err?.error?.erro ?? err?.message ?? 'Erro ao carregar itens.';
        this.carregando = false;
      }
    });
  }

  onVoltar(): void { this.voltar.emit(); }

  // ── Modal Transferir Conferência ─────────────────────────────────────────

  abrirModalTransferir(): void {
    this.modalTransferir = true;
    this.buscaTransferir = '';
    this.usuariosTransferir = [];
    this.selecionadoTransferir = null;
    this.erroTransferir = '';
  }

  fecharModalTransferir(): void {
    this.modalTransferir = false;
  }

  onBuscaTransferir(): void {
    clearTimeout(this._buscaTransferirTimer);
    if (this.buscaTransferir.trim().length < 2) {
      this.usuariosTransferir = [];
      return;
    }
    this._buscaTransferirTimer = setTimeout(() => {
      this.buscandoTransferir = true;
      this.gateway.buscarUsuariosConferencia(this.buscaTransferir).subscribe({
        next: (res: any) => {
          this.buscandoTransferir = false;
          const lista: any[] = res?.lista ?? [];
          this.usuariosTransferir = lista.map(u => ({ codusu: Number(u.codusu), nomeusu: u.nomeusu }));
        },
        error: () => { this.buscandoTransferir = false; }
      });
    }, 300);
  }

  selecionarTransferir(u: UsuarioConf): void {
    this.selecionadoTransferir = u;
    this.buscaTransferir = u.nomeusu;
    this.usuariosTransferir = [];
  }

  confirmarTransferencia(): void {
    if (this.transferindo || !this.selecionadoTransferir) return;
    this.transferindo = true;
    this.erroTransferir = '';
    this.gateway.transferirConferencia(this.nunota, this.selecionadoTransferir.codusu).subscribe({
      next: (res: any) => {
        this.transferindo = false;
        if (res?.sucesso) {
          this.modalTransferir = false;
          this.voltar.emit(); // não é mais o conferente desta nota
        } else {
          this.erroTransferir = res?.erro ?? 'Erro ao transferir conferência.';
        }
      },
      error: (err: any) => {
        this.transferindo = false;
        this.erroTransferir = err?.error?.erro ?? err?.message ?? 'Falha ao transferir.';
      }
    });
  }

  formatarQtd(n: number | null | undefined): string {
    return Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  /** Reconstrói qtdConferida/divergente a partir da nota gravada em
   *  "observacao" (única forma de persistir isso, já que o addon não tem
   *  campo próprio de quantidade conferida) — necessário para o estado
   *  sobreviver a um recarregamento de página. */
  private _reconstruirDivergencia(item: ConferenciaItem): void {
    const escapedNovo = EstoqueConferenciaItensComponent.MARCADOR_DIVERGENCIA.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(
      '(?:' + escapedNovo + '|' + EstoqueConferenciaItensComponent.MARCADOR_DIVERGENCIA_LEGADO_RE + ')' +
      '\\s*contado ([\\d.,]+), esperado [\\d.,]+ \\(dif: [^)]*\\)'
    , 'i');
    const m = (item.observacao || '').match(regex);
    if (m) {
      const contado = parseFloat(m[1].replace(/\./g, '').replace(',', '.'));
      item.qtdConferida = contado;
      item.qtdConferidaStr = this.formatarQtd(contado);
      item.divergente = Math.abs(contado - item.qtd) > 0.0001;
    } else {
      item.qtdConferida = null;
      item.qtdConferidaStr = '';
      item.divergente = false;
    }
  }

  /** Item escolhido diretamente por clique num candidato (ver selecionarCandidatoPn)
   *  — necessário quando o PN/código digitado bate com MAIS de uma linha pendente
   *  (produto duplicado no pedido, ex.: mesmo PN em dois locais/condições
   *  diferentes). Nesse caso o texto digitado NUNCA resolve sozinho pra um único
   *  item (ver itemBuscaAtual abaixo), então sem isso a tela ficava travada: o
   *  usuário clicava num candidato, o texto virava o código, mas esse código
   *  ainda batia com as DUAS linhas — voltava pra lista de candidatos de novo,
   *  em loop, sem nunca conseguir contar nenhuma delas. */
  private itemSelecionadoManual: ConferenciaItem | null = null;

  /**
   * Item encontrado para o termo digitado na busca por PN/código — só abre
   * o item (e libera a contagem) quando o termo digitado é EXATAMENTE igual
   * ao código do produto ou ao PN (refforn), nunca por casamento parcial.
   * Isso evita que digitar "1" já mostre um item só porque, por coincidência,
   * é a única correspondência parcial no momento — o conferente tem que
   * digitar (ou escolher, via itensBuscaMultiplos) o código/PN completo.
   */
  get itemBuscaAtual(): ConferenciaItem | null {
    // Seleção manual (usuário clicou num candidato específico pra desambiguar
    // PN duplicado) tem prioridade sobre o casamento por texto — só vale
    // enquanto o item escolhido continuar pendente.
    if (this.itemSelecionadoManual) {
      if (this.itensPendentes.includes(this.itemSelecionadoManual)) {
        return this.itemSelecionadoManual;
      }
      this.itemSelecionadoManual = null;
    }

    const termo = this.buscaPn.trim();
    if (!termo) return null;
    const termoLower = termo.toLowerCase();

    const exatos = this.itensPendentes.filter(i =>
      String(i.codprod) === termo ||
      (!!i.refforn && i.refforn.trim().toLowerCase() === termoLower)
    );
    return exatos.length === 1 ? exatos[0] : null;
  }

  /** Candidatos parciais, exibidos apenas como sugestão pra clicar — nunca
   *  abrem o item sozinhos. Só considera código do produto ou PN (refforn);
   *  nunca a descrição, senão qualquer letra digitada "acha" algum item pela
   *  descrição e quebra a busca cega (o conferente não pode achar pelo nome).
   *  Só começa a sugerir a partir de 2 caracteres, pra não listar tudo a
   *  cada tecla. Quando o produto está duplicado no pedido (mesmo código/PN em
   *  mais de uma linha pendente), a lista mostra as duas — ver selecionarCandidatoPn
   *  pra como o clique escolhe a linha certa mesmo com texto idêntico. */
  get itensBuscaMultiplos(): ConferenciaItem[] {
    const termo = this.buscaPn.trim().toLowerCase();
    if (termo.length < 2 || this.itemBuscaAtual) return [];
    return this.itensPendentes.filter(i =>
      String(i.codprod).toLowerCase().includes(termo) ||
      (!!i.refforn && i.refforn.toLowerCase().includes(termo))
    ).slice(0, 8);
  }

  /** true quando existe mais de uma linha PENDENTE com o mesmo código de
   *  produto que "item" — usado só pra decidir se mostra um diferenciador
   *  extra (seq/local) nos candidatos, já que nesse caso código e PN sozinhos
   *  não bastam pra identificar qual linha é qual. */
  candidatoTemDuplicata(item: ConferenciaItem): boolean {
    return this.itensPendentes.filter(i => i.codprod === item.codprod).length > 1;
  }

  /** Disparado pela digitação real no campo de busca (ngModelChange do input)
   *  — limpa qualquer seleção manual anterior (ver itemSelecionadoManual),
   *  porque o usuário está começando uma busca nova por texto. */
  onBuscaPnChange(): void {
    this.itemSelecionadoManual = null;
    this._focarQtdSeMatch();
  }

  /** Quando o termo/seleção atual já resolve pra um único item, foca
   *  automaticamente o campo de quantidade contada, pra o fluxo ficar: digita
   *  PN (ou clica um candidato) → aparece o item → digita quantidade →
   *  confirma, sem precisar tocar no mouse. */
  private _focarQtdSeMatch(): void {
    setTimeout(() => {
      if (this.itemBuscaAtual) {
        this.qtdContadaInputRef?.nativeElement.focus();
      }
    }, 0);
  }

  /** Usuário escolheu um dos candidatos na lista de desambiguação — seleciona
   *  o ITEM diretamente (não só o texto), pra funcionar mesmo quando duas
   *  linhas pendentes compartilham o mesmo código/PN (produto duplicado no
   *  pedido): nesse caso, preencher o campo com o código não seria suficiente
   *  pra apontar pra UMA linha específica, porque o texto continuaria batendo
   *  com as duas ao mesmo tempo. */
  selecionarCandidatoPn(item: ConferenciaItem): void {
    this.itemSelecionadoManual = item;
    this.buscaPn = String(item.codprod);
    this._focarQtdSeMatch();
  }

  /** Confirma a contagem vinda do fluxo de busca por PN: reaproveita
   *  confirmarContagem e, se salvar com sucesso, limpa a busca e devolve
   *  o foco pro campo de PN — pronto pro próximo item. */
  confirmarContagemBusca(item: ConferenciaItem): void {
    this.confirmarContagem(item, () => {
      this.buscaPn = '';
      this.itemSelecionadoManual = null;
      setTimeout(() => this.buscaPnInputRef?.nativeElement.focus(), 0);
    });
  }

  private _removerNotaDivergencia(observacao: string): string {
    const escapedNovo = EstoqueConferenciaItensComponent.MARCADOR_DIVERGENCIA.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(
      '\\n?(?:' + escapedNovo + '|' + EstoqueConferenciaItensComponent.MARCADOR_DIVERGENCIA_LEGADO_RE + ')' +
      '[^\\n]*'
    , 'i');
    return (observacao || '').replace(regex, '').trim();
  }

  /**
   * Confirma a contagem cega do item: o conferente digita a quantidade que
   * contou (sem ver a quantidade esperada) e aqui comparamos com item.qtd.
   * Se divergir, o item ainda é marcado como conferido (foi processado),
   * mas fica sinalizado como divergente para o resumo final.
   */
  confirmarContagem(item: ConferenciaItem, aposSalvar?: () => void): void {
    const raw = (item.qtdConferidaStr ?? '').replace(',', '.').trim();
    const num = parseFloat(raw);
    if (isNaN(num) || num < 0) {
      this.erroSalvar = 'Informe a quantidade contada.';
      return;
    }

    this.erroSalvar = '';
    const diff = Number((num - item.qtd).toFixed(3));
    const divergente = Math.abs(diff) > 0.0001;

    const obsBase = this._removerNotaDivergencia(item.observacao);
    let novaObs = obsBase;
    if (divergente) {
      const nota = `${EstoqueConferenciaItensComponent.MARCADOR_DIVERGENCIA} contado ${this.formatarQtd(num)}, esperado ${this.formatarQtd(item.qtd)} (dif: ${diff > 0 ? '+' : ''}${this.formatarQtd(diff)})`;
      novaObs = obsBase ? `${nota}\n${obsBase}` : nota;
    }

    const qtdConferidaAnterior = item.qtdConferida;
    const divergenteAnterior   = item.divergente;
    const obsAnterior          = item.observacao;
    const conferidoAnterior    = item.conferido;

    item.qtdConferida = num;
    item.divergente   = divergente;
    item.observacao    = novaObs;
    item.conferido      = true;
    if (novaObs) this.obsEditando.add(item.seq);

    this.gateway.salvarItemConferencia(this.nunota, item.seq, true, novaObs || undefined).subscribe({
      next: (res: any) => {
        if (!res?.sucesso) {
          item.qtdConferida = qtdConferidaAnterior;
          item.divergente   = divergenteAnterior;
          item.observacao    = obsAnterior;
          item.conferido      = conferidoAnterior;
          this.erroSalvar = res?.erro ?? 'Erro ao salvar item.';
          return;
        }
        if (res.allDone !== undefined) this.allDone = res.allDone;
        if (this.itensPendentes.length === 0) {
          this.abaAtiva = 'conferidos';
          if (this.allDone) this._abrirModalFinalOuDivergencia();
        }
        if (aposSalvar) aposSalvar();
      },
      error: (err: any) => {
        item.qtdConferida = qtdConferidaAnterior;
        item.divergente   = divergenteAnterior;
        item.observacao    = obsAnterior;
        item.conferido      = conferidoAnterior;
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao salvar item.';
      }
    });
  }

  /** Desfaz a conferência de um item (aba Conferidos → X), voltando pra
   *  "A Conferir" e limpando a contagem/divergência gravada. */
  desconferirItem(item: ConferenciaItem): void {
    // Item pendente ("Item no outro Galpão") só sai desse estado via Desfazer
    // (desfazerPendenciaGalpao) ou sendo resolvido na tela global "Itens
    // Pendentes" -- clique no card não desconfere por engano uma pendência aberta.
    if (item.pendente) return;
    const obsAnterior          = item.observacao;
    const qtdConferidaAnterior = item.qtdConferida;
    const divergenteAnterior   = item.divergente;

    const novaObs = this._removerNotaDivergencia(item.observacao);
    item.conferido     = false;
    item.qtdConferida  = null;
    item.qtdConferidaStr = '';
    item.divergente    = false;
    item.observacao     = novaObs;
    this.erroSalvar = '';

    this.gateway.salvarItemConferencia(this.nunota, item.seq, false, novaObs || undefined).subscribe({
      next: (res: any) => {
        if (!res?.sucesso) {
          item.conferido    = true;
          item.observacao    = obsAnterior;
          item.qtdConferida = qtdConferidaAnterior;
          item.divergente   = divergenteAnterior;
          this.erroSalvar = res?.erro ?? 'Erro ao salvar item.';
          return;
        }
        if (res.allDone !== undefined) this.allDone = res.allDone;
        if (this.abaAtiva === 'conferidos') this.abaAtiva = 'pendentes';
      },
      error: (err: any) => {
        item.conferido    = true;
        item.observacao    = obsAnterior;
        item.qtdConferida = qtdConferidaAnterior;
        item.divergente   = divergenteAnterior;
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao salvar item.';
      }
    });
  }

  private _abrirModalFinalOuDivergencia(): void {
    if (this.itensDivergentes.length > 0) {
      this.modalDivergencias = true;
    } else {
      this.modalFinalizar = true;
    }
  }

  get itensDivergentes(): ConferenciaItem[] {
    return this.itens.filter(i => i.divergente);
  }

  /** "Deixar Pendente" no modal de divergências — só fecha o modal, sem
   *  finalizar nem notificar; o pedido continua em CONFERÊNCIA pra ser
   *  revisado depois (pelo mesmo conferente ou outro). */
  deixarDivergenciasPendente(): void {
    this.modalDivergencias = false;
  }

  /** "Notificar Vendedor" no modal de divergências — reaproveita o mesmo
   *  fluxo de Enviar ao Vendedor já existente (as notas de divergência já
   *  estão gravadas na observação de cada item). */
  notificarVendedorDivergencias(): void {
    this.modalDivergencias = false;
    this.abrirModalPendencia();
  }

  salvarObservacao(item: ConferenciaItem): void {
    this.erroSalvar = '';
    this.gateway.salvarItemConferencia(this.nunota, item.seq, item.conferido, item.observacao || undefined).subscribe({
      next: (res: any) => {
        if (!res?.sucesso) this.erroSalvar = res?.erro ?? 'Erro ao salvar observação.';
      },
      error: (err: any) => {
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao salvar observação.';
      }
    });
  }

  toggleObs(item: ConferenciaItem): void {
    if (this.obsEditando.has(item.seq)) {
      this.obsEditando.delete(item.seq);
    } else {
      this.obsEditando.add(item.seq);
    }
  }

  get itensPendentes(): ConferenciaItem[] {
    return this.itens.filter(i => !i.conferido && !i.pendente);
  }

  /** Itens efetivamente conferidos OU sinalizados como pendentes ("Item no outro Galpão") -- ambos saem da fila "A Conferir". */
  get itensConferidosLista(): ConferenciaItem[] {
    return this.itens.filter(i => i.conferido || i.pendente);
  }

  get progresso(): number {
    if (!this.itens.length) return 0;
    return Math.round((this.itensConferidosLista.length / this.itens.length) * 100);
  }

  // ── Finalizar ────────────────────────────────────────────────────────────

  finalizarDoModal(): void {
    this.modalFinalizar = false;
    this.finalizar();
  }

  finalizar(): void {
    if (this.finalizando) return;
    // Trava dura: nunca finaliza com item divergente pendente de decisão,
    // mesmo que o botão do cabeçalho seja clicado direto (sem passar pelo
    // modal de conclusão). Redireciona pro modal de divergências, que é
    // onde o usuário escolhe "Deixar Pendente" ou "Notificar Vendedor".
    if (this.itensDivergentes.length > 0) {
      this.modalFinalizar = false;
      this.modalDivergencias = true;
      return;
    }
    this.finalizando = true;
    this.erroFinalizar = '';
    this.gateway.finalizarConferencia(this.nunota).subscribe({
      next: (res: any) => {
        if (res?.sucesso) {
          // Ainda "finalizando" — só termina de fato quando o gateway
          // aplicar o GPL_STATUS = PRONTO (silencioso) e chamar de volta
          // resolverStatusFinal(...).
          this.solicitarFinalizarComStatus.emit({ nunota: this.nunota, novoGplStatus: 'PRONTO' });
        } else {
          this.finalizando = false;
          this.erroFinalizar = res?.erro ?? 'Erro ao finalizar conferência.';
        }
      },
      error: (err: any) => {
        this.finalizando = false;
        this.erroFinalizar = err?.error?.erro ?? err?.message ?? 'Falha ao finalizar.';
      }
    });
  }

  /**
   * Chamado pelo gateway (via ViewChild) depois que a gravação silenciosa
   * de GPL_STATUS = "PRONTO" termina — só então a conferência é considerada
   * concluída de fato e a tela navega de volta pra fila. Se a gravação do
   * status falhar, a conferência em si já foi finalizada no Sankhya (o
   * addon nativo respondeu OK antes disso), então ainda assim segue em
   * frente, só avisando que o rótulo de status pode ter ficado divergente.
   */
  resolverStatusFinal(sucesso: boolean, mensagem?: string): void {
    const eraClienteRetira = this.finalizandoClienteRetira;
    this.finalizando = false;
    this.finalizandoClienteRetira = false;
    if (!sucesso) {
      const aviso = mensagem
        ? `Conferência finalizada, mas o status não pôde ser atualizado para Pronto: ${mensagem}`
        : 'Conferência finalizada, mas o status não pôde ser atualizado para Pronto.';
      if (eraClienteRetira) this.erroClienteRetira = aviso; else this.erroFinalizar = aviso;
    }
    this.finalizado.emit();
  }

  // ── Cliente Retira (finaliza direto para FINALIZADO) ────────────────────

  modalClienteRetira  = false;
  finalizandoClienteRetira = false;
  erroClienteRetira   = '';

  abrirModalClienteRetira(): void {
    if (!this.allDone) return;
    // Mesma trava do finalizar(): Cliente Retira também finaliza o pedido
    // (só que direto, sem passar pela logística), então não pode pular a
    // decisão sobre itens divergentes.
    if (this.itensDivergentes.length > 0) {
      this.modalDivergencias = true;
      return;
    }
    this.erroClienteRetira  = '';
    this.modalClienteRetira = true;
  }

  fecharModalClienteRetira(): void {
    if (this.finalizandoClienteRetira) return;
    this.modalClienteRetira = false;
  }

  confirmarClienteRetira(): void {
    if (this.finalizandoClienteRetira) return;
    this.finalizandoClienteRetira = true;
    this.erroClienteRetira        = '';
    this.modalClienteRetira       = false;
    this.gateway.finalizarConferenciaClienteRetira(this.nunota).subscribe({
      next: (res: any) => {
        if (res?.sucesso) {
          // Idem finalizar(): só termina quando resolverStatusFinal(...) for
          // chamado pelo gateway, depois do GPL_STATUS = PRONTO ser gravado.
          this.solicitarFinalizarComStatus.emit({ nunota: this.nunota, novoGplStatus: 'PRONTO' });
        } else {
          this.finalizandoClienteRetira = false;
          this.erroClienteRetira = res?.erro ?? 'Erro ao finalizar (cliente retira).';
        }
      },
      error: (err: any) => {
        this.finalizandoClienteRetira = false;
        this.erroClienteRetira = err?.error?.erro ?? err?.message ?? 'Falha ao finalizar (cliente retira).';
      }
    });
  }

  // ── Enviar Pendência ao Vendedor ──────────────────────────────────────────

  @Output() pendenciaEnviada = new EventEmitter<void>();

  enviandoPendencia   = false;
  erroPendencia       = '';
  modalPendencia      = false;
  /** Observação obrigatória explicando pro vendedor o motivo do envio
   *  (divergência, item faltando, etc.) — sem isso o vendedor não tem
   *  contexto nenhum sobre o que precisa ajustar. */
  obsPendencia        = '';

  get temItensPendentes(): boolean {
    return this.itensPendentes.length > 0;
  }

  /** Gate real do botão/modal "Enviar ao Vendedor" — também libera quando
   *  há itens divergentes (contagem cega bateu diferente do esperado),
   *  não só quando há itens ainda não conferidos. */
  get temItensPendentesOuDivergentes(): boolean {
    return this.itensPendentes.length > 0 || this.itensDivergentes.length > 0;
  }

  get textoItensPendenciaModal(): string {
    const totalPend = this.itensPendentes.length;
    const totalDiv  = this.itensDivergentes.length;
    const partes: string[] = [];
    if (totalPend > 0) partes.push(`${totalPend} ${totalPend === 1 ? 'item pendente' : 'itens pendentes'}`);
    if (totalDiv > 0) partes.push(`${totalDiv} ${totalDiv === 1 ? 'item com divergência de quantidade' : 'itens com divergência de quantidade'}`);
    return partes.length ? partes.join(' e ') : 'Nenhum item pendente';
  }

  // ── "Item no outro Galpão" (pendência que não trava a conferência cega) ──
  // Equivalente nativo do que já existe na Separação (ver
  // estoque-separacao-itens.component.ts) -- nomes com sufixo "Galpao" pra
  // não colidir com o modal "Enviar ao Vendedor" logo acima (modalPendencia/
  // erroPendencia/abrirModalPendencia já usados por aquele fluxo). Sem tela
  // de identificação: CODUSU/CODUSU_RESOLUCAO vêm sempre da sessão Sankhya
  // já logada.

  modalPendenciaGalpao = false;
  itemPendenciaGalpao: ConferenciaItem | null = null;
  pendenciaGalpaoObservacaoRascunho = '';
  marcandoPendenteGalpao = false;
  erroPendenciaGalpao = '';
  desfazendoPendenciaGalpaoSeq: number | null = null;

  /** Busca as pendências ABERTAS globais de Conferência e marca localmente os itens desta venda que têm uma. */
  private carregarPendenciasGalpao(): void {
    this.gateway.listarPendenciasConferencia().subscribe({
      next: (res: any) => {
        if (!res?.sucesso) return;
        const itens: any[] = res.itens ?? [];
        const seqsPendentes = new Set<number>(
          itens.filter(i => Number(i.nunota) === Number(this.nunota)).map(i => Number(i.sequencia))
        );
        this.itens.forEach(it => { it.pendente = seqsPendentes.has(it.seq); });
      },
      error: () => { /* falha silenciosa -- não impede a conferência de continuar */ }
    });
  }

  abrirModalPendenciaGalpao(item: ConferenciaItem, event: Event): void {
    event.stopPropagation();
    this.itemPendenciaGalpao = item;
    this.pendenciaGalpaoObservacaoRascunho = '';
    this.erroPendenciaGalpao = '';
    this.modalPendenciaGalpao = true;
  }

  fecharModalPendenciaGalpao(): void {
    if (this.marcandoPendenteGalpao) return;
    this.modalPendenciaGalpao = false;
    this.itemPendenciaGalpao = null;
    this.pendenciaGalpaoObservacaoRascunho = '';
  }

  confirmarPendenciaGalpao(): void {
    if (this.marcandoPendenteGalpao || !this.itemPendenciaGalpao) return;
    const item = this.itemPendenciaGalpao;
    this.marcandoPendenteGalpao = true;
    this.erroPendenciaGalpao = '';
    const texto = this.pendenciaGalpaoObservacaoRascunho.trim();
    this.gateway.sinalizarPendenciaConferencia(this.nunota, item.seq, texto || null).subscribe({
      next: (res: any) => {
        this.marcandoPendenteGalpao = false;
        if (!res?.sucesso) {
          this.erroPendenciaGalpao = res?.erro ?? 'Erro ao sinalizar pendência.';
          return;
        }
        item.pendente = true;
        this.modalPendenciaGalpao = false;
        this.itemPendenciaGalpao = null;
        this.pendenciaGalpaoObservacaoRascunho = '';
        this.buscaPn = '';
      },
      error: (err: any) => {
        this.marcandoPendenteGalpao = false;
        this.erroPendenciaGalpao = err?.error?.erro ?? err?.message ?? 'Erro ao sinalizar pendência.';
      }
    });
  }

  /** Desfaz uma sinalização feita por engano, dentro da mesma sessão de conferência. */
  desfazerPendenciaGalpao(item: ConferenciaItem, event: Event): void {
    event.stopPropagation();
    if (this.desfazendoPendenciaGalpaoSeq != null) return;
    this.desfazendoPendenciaGalpaoSeq = item.seq;
    this.gateway.desfazerPendenciaConferencia(this.nunota, item.seq).subscribe({
      next: (res: any) => {
        this.desfazendoPendenciaGalpaoSeq = null;
        if (!res?.sucesso) {
          this.erroSalvar = res?.erro ?? 'Erro ao desfazer pendência.';
          return;
        }
        item.pendente = false;
      },
      error: (err: any) => {
        this.desfazendoPendenciaGalpaoSeq = null;
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao desfazer pendência.';
      }
    });
  }

  abrirModalPendencia(): void {
    if (!this.temItensPendentesOuDivergentes) return;
    this.erroPendencia  = '';
    // Sugere um texto inicial a partir das divergências já contadas, mas o
    // conferente ainda precisa confirmar/completar — o campo continua
    // obrigatório (ver confirmarEnviarPendencia).
    const divergentes = this.itensDivergentes;
    this.obsPendencia = divergentes.length > 0
      ? divergentes.map(i => `${i.codprod} - ${i.descricao}: contado ${this.formatarQtd(i.qtdConferida)}, esperado ${this.formatarQtd(i.qtd)}`).join('\n')
      : '';
    this.modalPendencia = true;
  }

  fecharModalPendencia(): void {
    if (this.enviandoPendencia) return;
    this.modalPendencia = false;
  }

  confirmarEnviarPendencia(): void {
    if (this.enviandoPendencia) return;
    if (!this.obsPendencia.trim()) {
      this.erroPendencia = 'Descreva o motivo do envio antes de confirmar.';
      return;
    }
    this.enviandoPendencia = true;
    this.erroPendencia     = '';
    this.modalPendencia    = false;
    this.gateway.enviarPendenciaVendedor(this.nunota, this.obsPendencia.trim()).subscribe({
      next: (res: any) => {
        this.enviandoPendencia = false;
        if (res?.sucesso) {
          this.pendenciaEnviada.emit();
        } else {
          this.erroPendencia = res?.erro ?? 'Erro ao enviar pendência.';
        }
      },
      error: (err: any) => {
        this.enviandoPendencia = false;
        this.erroPendencia = err?.error?.erro ?? err?.message ?? 'Falha ao enviar pendência.';
      }
    });
  }
}
